/*
 * Free-gift picker for the cart page.
 *
 * <custom-gift-picker> is the dialog rendered by
 * snippets/custom-cart-gift-picker.liquid, one per gift_promotion block of
 * sections/main-cart-items.liquid. Opening is delegated at document level
 * because the "Select / Change your free gift" buttons live inside the
 * cart lines that cart.js re-renders on every line change. The opener
 * passes the current pick (data-gift-current = variant id, data-gift-replace
 * = its line key) so the dialog can mark it and swap it.
 *
 * Picking a card: if it is the current gift the dialog just closes;
 * otherwise the old gift line (if any) is removed through /cart/change.js
 * and the new one added through /cart/add.js with the same `sections` list
 * cart.js uses, painted the same way, followed by a cartUpdate publish with
 * source 'cart-items' so <cart-items> does not refetch on top of it. The
 * new line is tagged with the property `_gift_promo` = block id; the price
 * itself is zeroed by the automatic Buy X Get Y discount in the admin, not
 * by anything here.
 *
 * After every cart change the re-rendered lines are checked for a gift
 * caption carrying data-gift-excess (custom-cart-gift-line.liquid sets it
 * when the cart earns fewer gifts than it holds, e.g. the host teddy was
 * removed) and that gift line is trimmed by the excess through cart.js's
 * own updateQuantity, so the discount never silently lapses into a
 * full-price gift. Gift rows are hidden in the list (folded into their
 * host) but stay in the markup, which is what makes this work.
 */
if (!customElements.get('custom-gift-picker')) {
  class CustomGiftPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('[role="dialog"]');
      this.error = this.querySelector('.cart-gift-picker__error');
      this.items = Array.from(this.querySelectorAll('[data-gift-add]'));

      // A full re-render of <cart-items> brings a fresh copy; keep this one.
      document.querySelectorAll('custom-gift-picker').forEach((picker) => {
        if (picker !== this && picker.id === this.id) picker.remove();
      });

      this.querySelector('[data-gift-picker-close]').addEventListener('click', () => this.hide());
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
      this.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
      this.items.forEach((button) => {
        button.addEventListener('click', () => this.pick(button));
      });

      if (this.parentElement !== document.body) document.body.appendChild(this);
    }

    show(opener) {
      this.opener = opener;
      this.replaceKey = opener.dataset.giftReplace || null;
      this.currentId = opener.dataset.giftCurrent || null;
      this.items.forEach((button) => {
        const current = button.dataset.giftAdd === this.currentId;
        button.classList.toggle('is-current', current);
        button.setAttribute('aria-pressed', current ? 'true' : 'false');
      });
      this.showError('');
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      this.opener.setAttribute('aria-expanded', 'true');
      trapFocus(this, this.dialog);
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      this.opener?.setAttribute('aria-expanded', 'false');
      removeTrapFocus(this.opener?.isConnected ? this.opener : null);
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.items.forEach((button) => {
        if (busy) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    }

    pick(button) {
      if (this.classList.contains('is-busy')) return;
      if (button.dataset.giftAdd === this.currentId) {
        this.hide();
        return;
      }

      const cartItems = document.querySelector('cart-items');
      if (!cartItems) return;

      const sections = cartItems.getSectionsToRender();
      this.setBusy(true);
      this.showError('');

      const remove = this.replaceKey
        ? fetch(routes.cart_change_url, {
            ...fetchConfig(),
            body: JSON.stringify({ id: this.replaceKey, quantity: 0 }),
          }).then((response) => response.json())
        : Promise.resolve({});

      remove
        .then((state) => {
          if (state.status) throw new Error(state.description || state.message);
          return fetch(routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [
                {
                  id: Number(button.dataset.giftAdd),
                  quantity: 1,
                  properties: { _gift_promo: this.dataset.promo },
                },
              ],
              sections: sections.map((section) => section.section),
              sections_url: window.location.pathname,
            }),
          });
        })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) throw new Error(state.description || state.message);
          sections.forEach((section) => {
            const target = document.getElementById(section.id);
            if (!target || !state.sections?.[section.section]) return;
            const element = target.querySelector(section.selector) || target;
            element.innerHTML = cartItems.getSectionInnerHTML(state.sections[section.section], section.selector);
          });
          this.hide();
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
        })
        .catch((error) => {
          this.showError(error.message || window.cartStrings.error);
          // The old gift may already be gone; make the page agree with the cart.
          if (this.replaceKey) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'custom-gift-picker' });
        })
        .finally(() => this.setBusy(false));
    }
  }

  customElements.define('custom-gift-picker', CustomGiftPicker);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-gift-picker]');
    if (!opener) return;
    const picker = document.getElementById(opener.dataset.giftPicker);
    if (!picker) return;
    event.preventDefault();
    picker.show(opener);
  });

  const trimExcessGifts = () => {
    const marker = document.querySelector('[data-gift-excess]');
    if (!marker) return;
    const input = marker.closest('.cart-item')?.querySelector('.quantity__input');
    const cartItems = document.querySelector('cart-items');
    if (!input || !cartItems) return;

    const quantity = Math.max(0, Number(input.value) - Number(marker.dataset.giftExcess));
    // One request per re-render: the next paint recomputes the excess.
    marker.removeAttribute('data-gift-excess');
    cartItems.updateQuantity(input.dataset.index, quantity, { currentTarget: null }, undefined, input.dataset.quantityVariantId);
  };

  subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(trimExcessGifts, 0));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trimExcessGifts);
  } else {
    trimExcessGifts();
  }
}
